import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const ALL_FEATURES = [
  'dashboard','financial_management','inventory','warehouse_basic','purchase_orders',
  'sales','surat_jalan','reports_export','analytics_basic','production','bom','hutang_piutang',
  'variant_matrix','stock_opname','raw_material_tracking','bom_fashion','receipt_print','surat_jalan_fashion',
  'recipe_card','ingredient_tracking','menu_management','pos_fnb','thermal_receipt','payment_qris',
  'bom_multilevel','purchase_order_mfg','surat_jalan_mfg',
  'pos_retail','barcode_scan','receipt_digital','payment_multi','purchase_order_retail','supplier_payable',
  'fixed_assets','multi_user_3','sku_limit_500','marketplace_1_channel','channel_manager',
  'sku_per_variant','stock_per_variant','product_photo_variant','hangtag_print','cutting_order',
  'work_order_fashion','hpp_per_sku','marketplace_sync','marketplace_order_mgmt','pos_fashion',
  'courier_integration','return_customer','return_supplier','margin_per_sku','report_per_channel',
  'daily_ingredient_est','low_stock_alert_ingr','expiry_tracking','menu_promo','menu_seasonal',
  'bundle_package','table_management','split_bill','reservation','shift_management','online_delivery_order',
  'cogs_per_menu','report_per_menu','shift_report_cashier',
  'work_order','production_planning','actual_vs_target','scrap_tracking','quality_control','reject_tracking',
  'asset_register','goods_receipt','quotation_b2b','sales_order_b2b','invoice_faktur',
  'hpp_per_batch','production_cost_report',
  'cashier_closing','bulk_sku_management','price_tag_print','stock_opname_retail','customer_database',
  'purchase_history','marketplace_retail','online_order_management','goods_receipt_retail','return_to_supplier',
  'report_per_store','report_per_category','cash_reconciliation',
  'multi_user_10','sku_limit_2000','marketplace_all','multi_warehouse','api_key_generator','bulk_import','channel_manager_full',
  'preorder_management','reseller_management','digital_catalog','konsinyasi','sales_rep_tracking','komisi_reseller',
  'waste_tracking','qr_order','kitchen_display','gofood_grabfood_sync','catering_preorder','kitchen_cost_report',
  'capacity_planning','production_routing','labor_tracking','compliance_docs','machine_maintenance',
  'downtime_tracking','supplier_evaluation','contract_management','variance_analysis',
  'multi_store','multi_location_stock','reorder_point','loyalty_program','member_promo','omnichannel_stock_sync',
  'multi_user_unlimited','sku_unlimited','ai_insights_full','ai_cs_whatsapp','ai_automation_agent','priority_support',
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('stockara_token'));
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({});
  const [industry, setIndustry] = useState('');
  const [isImpersonating, setIsImpersonating] = useState(!!localStorage.getItem('stockara_master_token'));
  const [impersonationMeta, setImpersonationMeta] = useState(() => {
    try { return JSON.parse(localStorage.getItem('stockara_impersonation_meta') || '{}'); }
    catch (_) { return {}; }
  });

  const fetchUser = useCallback(async (tkn) => {
    const t = tkn || localStorage.getItem('stockara_token');
    if (!t) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setUser(res.data);
      if (!res.data.is_super_admin) {
        const [subRes, flagsRes] = await Promise.all([
          axios.get(`${API}/tenant/subscription`, { headers: { Authorization: `Bearer ${t}` } }),
          axios.get(`${API}/feature-flags`, { headers: { Authorization: `Bearer ${t}` } }),
        ]);
        setSubscription(subRes.data);
        setFeatureFlags(flagsRes.data);
        try {
          const profileRes = await axios.get(`${API}/tenant/company-profile`, { headers: { Authorization: `Bearer ${t}` } });
          setIndustry(profileRes.data.industry || '');
        } catch (_) {}
      } else {
        setSubscription({ tier: 'max', status: 'active', is_developer: true });
        setFeatureFlags(Object.fromEntries(ALL_FEATURES.map(f => [f, true])));
        setIndustry('all');
      }
    } catch (err) {
      localStorage.removeItem('stockara_token');
      setToken(null);
      setUser(null);
      setSubscription(null);
      setFeatureFlags({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('stockara_token', res.data.token);
    setToken(res.data.token);
    await fetchUser(res.data.token);
    return res.data;
  };

  const register = async (name, email, password, company_name) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, company_name });
    localStorage.setItem('stockara_token', res.data.token);
    setToken(res.data.token);
    await fetchUser(res.data.token);
    if (window.fbq && res.data.capi_event_id) {
      window.fbq('track', 'CompleteRegistration', { value: 0, currency: 'IDR' }, { eventID: res.data.capi_event_id });
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('stockara_token');
    localStorage.removeItem('stockara_master_token');
    localStorage.removeItem('stockara_impersonation_meta');
    setToken(null);
    setUser(null);
    setSubscription(null);
    setFeatureFlags({});
    setIsImpersonating(false);
    setImpersonationMeta({});
  };

  const startImpersonation = async (nextToken, meta = {}) => {
    const currentToken = localStorage.getItem('stockara_token');
    if (currentToken && !localStorage.getItem('stockara_master_token')) {
      localStorage.setItem('stockara_master_token', currentToken);
    }
    localStorage.setItem('stockara_token', nextToken);
    localStorage.setItem('stockara_impersonation_meta', JSON.stringify(meta));
    setIsImpersonating(true);
    setImpersonationMeta(meta);
    setToken(nextToken);
    await fetchUser(nextToken);
  };

  const stopImpersonation = async () => {
    const masterToken = localStorage.getItem('stockara_master_token');
    if (!masterToken) return logout();
    localStorage.setItem('stockara_token', masterToken);
    localStorage.removeItem('stockara_master_token');
    localStorage.removeItem('stockara_impersonation_meta');
    setIsImpersonating(false);
    setImpersonationMeta({});
    setToken(masterToken);
    await fetchUser(masterToken);
  };

  const updateProfile = async (data) => {
    const res = await axios.put(`${API}/auth/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(res.data);
    return res.data;
  };

  const refreshSubscription = async () => {
    if (!token || !user || user.is_super_admin) return;
    try {
      const [subRes, flagsRes] = await Promise.all([
        axios.get(`${API}/tenant/subscription`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/feature-flags`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSubscription(subRes.data);
      setFeatureFlags(flagsRes.data);
    } catch (_) {}
  };

  const isSuperAdmin = user?.is_super_admin === true;

  const canAccess = (featureName) => {
    if (isSuperAdmin) return true;
    if (!subscription || subscription.status === 'expired') return false;
    return featureFlags[featureName] === true;
  };

  const getTrialDaysRemaining = () => {
    if (!subscription || subscription.tier !== 'trial') return null;
    return subscription.days_remaining ?? null;
  };

  const isTrialExpired = () => subscription?.status === 'expired';

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      subscription, featureFlags,
      isSuperAdmin, canAccess,
      industry,
      isImpersonating, impersonationMeta,
      getTrialDaysRemaining, isTrialExpired,
      login, register, logout, startImpersonation, stopImpersonation,
      updateProfile, refreshSubscription, fetchUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
